window.BooSearchMatcher = {
  normalize(value) {
    return String(value || '')
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '');
  },

  distanceWithin(left, right, limit) {
    if (Math.abs(left.length - right.length) > limit) return false;

    let previous = Array.from({ length: right.length + 1 }, (_, index) => index);
    for (let row = 1; row <= left.length; row += 1) {
      const current = [row];
      let rowMinimum = current[0];

      for (let column = 1; column <= right.length; column += 1) {
        const cost = left[row - 1] === right[column - 1] ? 0 : 1;
        const value = Math.min(
          current[column - 1] + 1,
          previous[column] + 1,
          previous[column - 1] + cost
        );
        current[column] = value;
        rowMinimum = Math.min(rowMinimum, value);
      }

      if (rowMinimum > limit) return false;
      previous = current;
    }

    return previous[right.length] <= limit;
  },

  fuzzyFieldMatch(query, field) {
    if (!query || !field || query.length < 4) return false;
    const limit = Math.max(1, Math.floor(query.length * 0.2));
    if (field.length < query.length - limit) return false;

    for (let index = 0; index <= field.length - query.length + limit; index += 1) {
      const start = Math.max(0, index);
      const end = Math.min(field.length, index + query.length + limit);
      if (this.distanceWithin(query, field.slice(start, end), limit)) return true;
    }

    return false;
  },

  score(query, fields) {
    const normalizedQuery = this.normalize(query);
    if (normalizedQuery.length < 2) return 1;

    const strongFields = [fields.title, fields.handle].map((field) => this.normalize(field));
    const supportingFields = [fields.vendor, fields.type, fields.tags].map((field) => this.normalize(field));

    for (const field of strongFields) {
      if (!field) continue;
      if (field === normalizedQuery) return 1000;
      if (field.startsWith(normalizedQuery)) return 900;
      if (field.includes(normalizedQuery)) return 800;
    }

    for (const field of supportingFields) {
      if (field.includes(normalizedQuery)) return 200;
    }

    for (const field of strongFields) {
      if (this.fuzzyFieldMatch(normalizedQuery, field)) return 700;
    }

    return 0;
  },

  filterProductElements(root, query) {
    if (!root) return { productCount: 0, visibleProductCount: 0 };

    const products = Array.from(root.querySelectorAll('[data-search-result-product]'));
    if (!products.length) return { productCount: 0, visibleProductCount: 0 };

    const scores = products.map((product) => this.score(query, {
        title: product.dataset.searchTitle,
        handle: product.dataset.searchHandle,
        vendor: product.dataset.searchVendor,
        type: product.dataset.searchType,
        tags: product.dataset.searchTags,
      }));
    const hasStrongMatch = scores.some((score) => score >= 700);
    const visibleProductCount = products.reduce((count, product, index) => {
      const score = scores[index];
      const isMatch = hasStrongMatch ? score >= 700 : score > 0;
      product.hidden = !isMatch;
      product.setAttribute('aria-hidden', String(!isMatch));
      product.dataset.searchMatchScore = String(score);
      return count + (isMatch ? 1 : 0);
    }, 0);

    return { productCount: products.length, visibleProductCount };
  },
};

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);
